import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Label, Card } from "@/components/ui";
import { BookOpen, Mail } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/lib/auth-context";
import { API_BASE } from "@/lib/api";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof loginSchema>;

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function Login() {
  const [, setLocation] = useLocation();
  const { login: setAuthToken } = useAuth();
  const [serverError, setServerError] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [isGooglePending, setIsGooglePending] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setServerError("");
    setUnverifiedEmail("");
    setIsPending(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();

      if (res.status === 403 && json.error === "Unverified") {
        setUnverifiedEmail(json.email || data.email);
        return;
      }

      if (!res.ok) {
        setServerError(json.message || "Invalid credentials");
        return;
      }

      setAuthToken(json.token);
      setLocation("/dashboard");
    } catch {
      setServerError("Network error. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  const handleGoogleSuccess = async (response: any) => {
    setIsGooglePending(true);
    setServerError("");
    setUnverifiedEmail("");
    try {
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await res.json();
      if (!res.ok) {
        setServerError(data.message || "Google sign-in failed.");
        return;
      }
      setAuthToken(data.token);
      setLocation("/dashboard");
    } catch {
      setServerError("Google sign-in failed. Please try again.");
    } finally {
      setIsGooglePending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-4 bg-background relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-primary/10 rounded-full blur-[80px] -z-10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-accent/10 rounded-full blur-[80px] -z-10" />

      <Link href="/">
        <div className="flex items-center gap-3 mb-10 cursor-pointer hover:opacity-80 transition-opacity">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <span className="font-display font-bold text-3xl tracking-tight text-foreground">Lerni</span>
        </div>
      </Link>

      <Card className="w-full max-w-md p-8 glass-panel">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold font-display">Welcome back</h1>
          <p className="text-muted-foreground mt-2">Enter your credentials to access your account</p>
        </div>

        {serverError && (
          <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm font-medium text-center mb-5">
            {serverError}
          </div>
        )}

        {unverifiedEmail && (
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-5">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Email not verified</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                  Please verify your email before logging in.
                </p>
                <button
                  onClick={() => setLocation(`/verify-email?email=${encodeURIComponent(unverifiedEmail)}`)}
                  className="text-xs font-bold text-amber-700 dark:text-amber-300 underline mt-1.5 block"
                >
                  Enter verification code →
                </button>
              </div>
            </div>
          </div>
        )}

        {GOOGLE_CLIENT_ID && (
          <>
            <div className="flex justify-center mb-4">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setServerError("Google sign-in failed. Please try again.")}
                text="signin_with"
                shape="rectangular"
                theme="outline"
                size="large"
                width="368"
              />
            </div>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground font-medium">or</span>
              </div>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" placeholder="you@example.com" {...register("email")} />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" placeholder="••••••••" {...register("password")} />
            {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
          </div>

          <Button type="submit" className="w-full h-12 text-lg mt-2" disabled={isPending || isGooglePending}>
            {isPending ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <p className="mt-8 text-center text-muted-foreground">
          Don't have an account?{" "}
          <Link href="/register" className="text-primary font-semibold hover:underline">
            Sign up
          </Link>
        </p>
      </Card>
    </div>
  );
}
